import React, { useState, useRef, useLayoutEffect } from 'react';
import { CircleAlert } from 'lucide-react';
import { C, G } from './uiTokens';

interface GlobalTextAreaProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  rows?: number;
  autoResize?: boolean;
  fullWidth?: boolean;
  maxLength?: number;
  name?: string;
  id?: string;
  /** red border without a message (e.g. when the error is shown elsewhere) */
  invalid?: boolean;
  /** content aligned to the right of the label, e.g. a helper link */
  labelRight?: React.ReactNode;
  /** ref to the underlying <textarea> (focus management) */
  inputRef?: React.Ref<HTMLTextAreaElement>;
  /** extra native textarea attributes (autoCapitalize, spellCheck, ...) */
  inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

const FONT_PX = 13.3; // 0.83rem
const LINE_PX = FONT_PX * 1.5;
const PAD_Y = 9.6;

export const GlobalTextArea: React.FC<GlobalTextAreaProps> = ({
  label,
  value,
  onChange,
  onBlur,
  onKeyDown,
  onKeyUp,
  placeholder = '',
  required = false,
  disabled = false,
  error = '',
  hint,
  rows = 4,
  autoResize = true,
  fullWidth = true,
  maxLength,
  name,
  id,
  invalid = false,
  labelRight,
  inputRef,
  inputProps,
}) => {
  const [focused, setFocused] = useState(false);
  const localRef = useRef<HTMLTextAreaElement>(null);

  // Merge external ref with local ref used for auto-resize
  const setRefs = (el: HTMLTextAreaElement | null) => {
    localRef.current = el;
    if (!inputRef) return;
    if (typeof inputRef === 'function') {
      inputRef(el);
    } else {
      (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    }
  };

  // grow with content when autoResize is on
  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el || !autoResize) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value, autoResize]);

  const hasError = !!error || invalid;
  const borderColor = hasError ? C.danger : focused ? 'rgba(17,85,80,0.5)' : G.line;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: '5.9px',
          }}
        >
          <label
            htmlFor={id}
            style={{
              display: 'block',
              fontSize: '0.73rem',
              fontWeight: 600,
              color: hasError ? C.danger : '#374151',
            }}
          >
            {label}
            {required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
          </label>
          {labelRight}
        </div>
      )}

      <textarea
        {...inputProps}
        ref={setRefs}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        aria-invalid={hasError}
        className="global-textarea"
        style={{
          display: 'block',
          width: '100%',
          boxSizing: 'border-box',
          padding: `${PAD_Y}px 11.8px`,
          border: `1px solid ${borderColor}`,
          borderRadius: '8.9px',
          fontSize: '0.83rem',
          fontWeight: 500,
          fontFamily: 'inherit',
          lineHeight: 1.5,
          color: disabled ? C.muted : '#1f2937',
          background: disabled ? '#f9fafb' : 'white',
          minHeight: `${rows * LINE_PX + PAD_Y * 2 + 2}px`,
          resize: autoResize ? 'none' : 'vertical',
          overflowY: autoResize ? 'hidden' : 'auto',
          outline: 'none',
          boxShadow: focused
            ? `0 0 0 3px ${hasError ? 'rgba(220,38,38,0.1)' : 'rgba(17,85,80,0.08)'}`
            : 'none',
          transition: 'border-color 0.18s, box-shadow 0.18s',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />

      {(error || hint || maxLength) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            marginTop: '5.4px',
          }}
        >
          {error ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4.3px',
                fontSize: '0.71rem',
                color: C.danger,
                fontWeight: 500,
              }}
            >
              <CircleAlert size={12} /> {error}
            </span>
          ) : hint ? (
            <span style={{ fontSize: '0.71rem', color: C.sub }}>{hint}</span>
          ) : (
            <span />
          )}

          {maxLength != null && (
            <span
              style={{
                fontSize: '0.71rem',
                color: value.length >= maxLength ? C.danger : C.muted,
                fontWeight: 500,
              }}
            >
              {value.length}/{maxLength}
            </span>
          )}
        </div>
      )}

      <style>{`
        .global-textarea::placeholder { color: rgba(17,85,80,0.3); }
        .global-textarea:enabled:hover { border-color: rgba(17,85,80,0.3); }
        .global-textarea::-webkit-scrollbar { width: 6px; }
        .global-textarea::-webkit-scrollbar-track { background: transparent; }
        .global-textarea::-webkit-scrollbar-thumb { background: rgba(17,85,80,0.2); border-radius: 3px; }
        .global-textarea::-webkit-scrollbar-thumb:hover { background: ${C.teal}; }
      `}</style>
    </div>
  );
};